import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Sparkles, Heart, ChevronDown, Loader2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PregnancyIssueHelper() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openKey, setOpenKey] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/wellness/pregnancy/herbs/issues`)
      .then((r) => setData(r.data?.issues || {}))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-pink-300 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="pregnancy-issue-helper-page">
      <motion.header
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-pink-500/15 border border-pink-400/20 text-pink-200 text-xs uppercase tracking-widest mb-3">
          <Sparkles className="w-3.5 h-3.5" /> Issue Helper
        </div>
        <h1 className="text-3xl sm:text-4xl font-serif text-white">Gentle remedies for common issues</h1>
        <p className="text-mystic-200 text-sm mt-2 max-w-xl">
          Pregnancy-safe herbs and lifestyle tips for the bumps along the way. Always pair with your provider's guidance.
        </p>
      </motion.header>

      <div className="rounded-2xl border border-amber-400/30 bg-amber-500/10 p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
        <p className="text-amber-100 text-sm leading-relaxed">
          This is informational only. Talk to your OB-GYN, midwife, or pharmacist before starting any new herb during pregnancy.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(data || {}).map(([key, item]) => {
          const isOpen = openKey === key;
          return (
            <motion.div
              key={key}
              layout
              transition={{ duration: 0.3 }}
              data-testid={`issue-card-${key}`}
            >
              <Card
                className={`glass border transition-all cursor-pointer ${
                  isOpen ? "border-pink-400/50" : "border-white/10 hover:border-pink-400/30"
                }`}
                onClick={() => setOpenKey(isOpen ? null : key)}
              >
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl" aria-hidden>{item.icon}</span>
                      <h3 className="text-white font-semibold">{item.issue}</h3>
                    </div>
                    <ChevronDown
                      className={`w-5 h-5 text-mystic-300 transition-transform ${isOpen ? "rotate-180" : ""}`}
                    />
                  </div>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25 }}
                        className="overflow-hidden"
                      >
                        <div className="pt-4 mt-4 border-t border-white/10 space-y-4">
                          <section>
                            <p className="text-xs uppercase tracking-wider text-pink-200 mb-2 flex items-center gap-1.5">
                              <Sparkles className="w-3 h-3" /> Safe herbs
                            </p>
                            <ul className="space-y-2">
                              {item.safe_herbs.map((h) => (
                                <li key={h.name} className="text-sm text-mystic-100">
                                  <span className="text-white font-medium">{h.name}</span>
                                  <span className="text-mystic-300"> · {h.use}</span>
                                  <span className="ml-2 inline-block px-2 py-0.5 rounded-full bg-white/5 text-mystic-300 text-xs">
                                    {h.trimester}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </section>
                          <section>
                            <p className="text-xs uppercase tracking-wider text-rose-200 mb-2 flex items-center gap-1.5">
                              <Heart className="w-3 h-3" /> Lifestyle
                            </p>
                            <ul className="space-y-1">
                              {item.lifestyle.map((tip, i) => (
                                <li key={i} className="text-sm text-mystic-100 flex items-start gap-2">
                                  <span className="text-pink-300">•</span>
                                  <span>{tip}</span>
                                </li>
                              ))}
                            </ul>
                          </section>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
